class FwdNavigation extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.fwd-nav__item--has-children')].filter((item) =>
      item.querySelector(':scope > .fwd-nav__parent-row .fwd-nav__toggle')
    );
    this.pointerHandlers = [];
    this.toggleHandlers = [];
    this.clickOpenItem = null;
    this.closeTimer = null;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.items.forEach((item) => {
      const toggle = item.querySelector(':scope > .fwd-nav__parent-row .fwd-nav__toggle');
      const toggleFromClick = (event) => {
        event.stopPropagation();

        if (this.clickOpenItem === item && item.classList.contains('is-open')) {
          this.clickOpenItem = null;
          this.setItemState(item, false);
          return;
        }

        this.closeAll(item);
        this.setItemState(item, true);
        this.clickOpenItem = item;
      };
      toggle.addEventListener('click', toggleFromClick);
      this.toggleHandlers.push({ toggle, toggleFromClick });

      const openFromPointer = () => {
        if (!window.matchMedia('(min-width: 990px) and (hover: hover)').matches) return;
        window.clearTimeout(this.closeTimer);
        if (this.clickOpenItem === item) return;
        this.clickOpenItem = null;
        this.closeAll(item);
        this.setItemState(item, true);
      };
      const closeFromPointer = () => {
        if (!window.matchMedia('(min-width: 990px) and (hover: hover)').matches) return;
        if (this.clickOpenItem === item) return;
        window.clearTimeout(this.closeTimer);
        this.closeTimer = window.setTimeout(() => this.closeAll(), 180);
      };
      item.addEventListener('pointerenter', openFromPointer);
      item.addEventListener('pointerleave', closeFromPointer);
      this.pointerHandlers.push({ item, openFromPointer, closeFromPointer });
    });

    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    window.clearTimeout(this.closeTimer);
    this.pointerHandlers.forEach(({ item, openFromPointer, closeFromPointer }) => {
      item.removeEventListener('pointerenter', openFromPointer);
      item.removeEventListener('pointerleave', closeFromPointer);
    });
    this.toggleHandlers.forEach(({ toggle, toggleFromClick }) => {
      toggle.removeEventListener('click', toggleFromClick);
    });
    document.removeEventListener('click', this.onDocumentClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  closeAll(except) {
    this.items.forEach((item) => {
      if (item === except) return;
      this.setItemState(item, false);
    });
    if (this.clickOpenItem && this.clickOpenItem !== except) this.clickOpenItem = null;
  }

  setItemState(item, isOpen) {
    const toggle = item.querySelector(':scope > .fwd-nav__parent-row .fwd-nav__toggle');
    const label = toggle?.querySelector('.fwd-nav__toggle-label')?.textContent.trim() || '메뉴';
    item.classList.toggle('is-open', isOpen);
    toggle?.setAttribute('aria-expanded', String(isOpen));
    toggle?.setAttribute('aria-label', `${label} 하위 메뉴 ${isOpen ? '닫기' : '열기'}`);
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.clickOpenItem = null;
      this.closeAll();
    }
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    const openItem = this.querySelector('.fwd-nav__item.is-open');
    if (!openItem) return;
    const toggle = openItem.querySelector('.fwd-nav__toggle');
    this.clickOpenItem = null;
    this.closeAll();
    toggle?.focus();
  }
}

class FwdCatalogCount extends HTMLElement {
  static requests = new Map();
  static pageRequests = new Map();
  static entitySummaryRequests = new Map();
  static discoverSummaryRequests = new Map();
  static maxRequestAttempts = 3;
  static maxConcurrentPageRequests = 2;
  static maxConcurrentRequests = 2;
  static retryDelayMilliseconds = 100;
  static cacheSchemaVersion = 1;
  static productCacheTtl = 5 * 60 * 1000;
  static entityCacheTtl = 15 * 60 * 1000;
  static discoverSummaryCacheTtl = 15 * 60 * 1000;
  static staleCacheLimit = 60 * 60 * 1000;
  static challengeCooldown = 10 * 60 * 1000;
  static activeRequests = 0;
  static requestQueue = [];
  static challengeUntil = 0;

  connectedCallback() {
    if (this.dataset.desktopOnly === 'true' && this.deferUntilDesktop()) return;
    if (this.dataset.loadWhenVisible === 'true' && this.deferUntilVisible()) return;
    this.connect();
  }

  deferUntilDesktop() {
    this.desktopMedia = window.matchMedia('(min-width: 990px)');
    if (this.desktopMedia.matches) return false;

    this.dataset.state = 'deferred';
    this.hidden = true;
    this.handleDesktopMediaChange = (event) => {
      if (!event.matches) return;
      this.removeDesktopMediaListener();
      this.connect();
    };
    if (typeof this.desktopMedia.addEventListener === 'function') {
      this.desktopMedia.addEventListener('change', this.handleDesktopMediaChange);
    } else {
      this.desktopMedia.addListener?.(this.handleDesktopMediaChange);
    }
    return true;
  }

  removeDesktopMediaListener() {
    if (!this.desktopMedia || !this.handleDesktopMediaChange) return;
    if (typeof this.desktopMedia.removeEventListener === 'function') {
      this.desktopMedia.removeEventListener('change', this.handleDesktopMediaChange);
    } else {
      this.desktopMedia.removeListener?.(this.handleDesktopMediaChange);
    }
    this.handleDesktopMediaChange = null;
  }

  connect() {
    const entity = this.dataset.entity;
    if (entity === 'brand' || entity === 'country') {
      if (!this.dataset.summaryEndpoint
        && (!this.dataset.directoryEndpoint || !this.dataset.productsEndpoint)) {
        this.setUnavailableState(entity);
        return;
      }
      this.connectEntityCount(entity);
      return;
    }

    const endpoint = this.dataset.endpoint;
    if (!endpoint) {
      this.setUnavailableState(entity);
      return;
    }
    const totalEndpoint = this.dataset.totalEndpoint;
    const requestKey = totalEndpoint ? `${endpoint}|total:${totalEndpoint}` : endpoint;
    this.connectCount({
      entity,
      requestKey,
      storageKey: FwdCatalogCount.storageKey('product', [endpoint, totalEndpoint]),
      ttl: FwdCatalogCount.productCacheTtl,
      loader: () => FwdCatalogCount.fetchProductCount(endpoint, totalEndpoint),
    });
  }

  deferUntilVisible() {
    if (typeof window.IntersectionObserver !== 'function') return false;
    const target = this.closest?.('li') || this.parentElement;
    if (!target) return false;

    this.dataset.state = 'deferred';
    this.visibilityObserver = new window.IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
      this.connect();
    });
    this.visibilityObserver.observe(target);
    return true;
  }

  connectEntityCount(entity) {
    const summaryEndpoint = this.dataset.summaryEndpoint;
    if (summaryEndpoint) {
      const { cached, request } = FwdCatalogCount.getDiscoverSummary(summaryEndpoint);
      if (cached) this.renderCount(cached.summary[entity], entity, cached.fresh ? 'loaded' : 'stale');
      if (cached?.fresh || FwdCatalogCount.challengeIsActive()) {
        if (!cached) this.setUnavailableState(entity);
        return;
      }
      if (!cached) this.setLoadingState(entity);
      this.hydrate(request.then((summary) => summary[entity]), entity, Boolean(cached));
      return;
    }

    const directoryEndpoint = this.dataset.directoryEndpoint;
    const productsEndpoint = this.dataset.productsEndpoint;
    if (!directoryEndpoint || !productsEndpoint) return;

    this.connectCount({
      entity,
      requestKey: `entity:${entity}:${directoryEndpoint}:${productsEndpoint}`,
      storageKey: FwdCatalogCount.storageKey(`entity-${entity}`, [directoryEndpoint, productsEndpoint]),
      ttl: FwdCatalogCount.entityCacheTtl,
      loader: () => FwdCatalogCount.fetchEntityCount(entity, directoryEndpoint, productsEndpoint),
    });
  }

  connectCount({ entity, requestKey, storageKey, ttl, loader }) {
    const cached = FwdCatalogCount.readStoredCount(storageKey, ttl);
    if (cached) this.renderCount(cached.count, entity, cached.fresh ? 'loaded' : 'stale');
    if (cached?.fresh || FwdCatalogCount.challengeIsActive()) {
      if (!cached) this.setUnavailableState(entity);
      return;
    }
    if (!cached) this.setLoadingState(entity);

    const request = FwdCatalogCount.cachedRequest(
      FwdCatalogCount.requests,
      requestKey,
      () => loader().then((count) => {
        FwdCatalogCount.writeStoredCount(storageKey, count);
        return count;
      })
    );
    this.hydrate(request, entity, Boolean(cached));
  }

  hydrate(request, entity, preserveCurrentOnError = false) {
    request.then((count) => {
      if (!this.isConnected) return;
      this.renderCount(count, entity, 'loaded');
    }).catch(() => {
      if (!this.isConnected) return;
      if (!preserveCurrentOnError) this.setUnavailableState(entity);
    });
  }

  renderCount(count, entity, state) {
    this.textContent = `(${count.available}/${count.total})`;
    const entityLabel = entity === 'brand' ? '브랜드' : entity === 'country' ? '제조국' : '';
    this.setAttribute(
      'aria-label',
      entityLabel
        ? `판매 가능 ${entityLabel} ${count.available}, 전체 ${entityLabel} ${count.total}`
        : `판매 가능 ${count.available}, 전체 ${count.total}`
    );
    this.dataset.state = state;
    this.setAttribute('aria-busy', 'false');
    this.hidden = false;
    this.enableLink();
    if (state === 'loaded' && count.total === 0) this.disableLink();
  }

  setLoadingState(entity) {
    this.enableLink();
    const entityLabel = entity === 'brand' ? '브랜드' : entity === 'country' ? '제조국' : '상품';
    this.textContent = '(…/…)';
    this.dataset.state = 'loading';
    this.setAttribute('aria-busy', 'true');
    this.setAttribute('aria-label', `${entityLabel} 수를 불러오는 중`);
    this.hidden = false;
  }

  setUnavailableState(entity) {
    this.enableLink();
    const entityLabel = entity === 'brand' ? '브랜드' : entity === 'country' ? '제조국' : '상품';
    this.textContent = '(—/—)';
    this.dataset.state = 'unavailable';
    this.setAttribute('aria-busy', 'false');
    this.setAttribute('aria-label', `${entityLabel} 수를 불러오지 못했습니다`);
    this.hidden = false;
  }

  disconnectedCallback() {
    this.removeDesktopMediaListener();
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = null;
    if (!this.disabledLink || !this.blockDisabledNavigation) return;
    this.disabledLink.removeEventListener('click', this.blockDisabledNavigation);
    this.disabledLink.removeEventListener('keydown', this.blockDisabledNavigation);
    this.disabledLink = null;
    this.blockDisabledNavigation = null;
  }

  disableLink() {
    const link = this.previousElementSibling;
    if (!link || link.tagName !== 'A' || this.disabledLink === link) return;

    this.blockDisabledNavigation = (event) => {
      if (event.type === 'click' || event.key === 'Enter') event.preventDefault();
    };
    this.disabledLink = link;
    link.setAttribute('aria-disabled', 'true');
    link.addEventListener('click', this.blockDisabledNavigation);
    link.addEventListener('keydown', this.blockDisabledNavigation);
  }

  enableLink() {
    if (!this.disabledLink || !this.blockDisabledNavigation) return;
    this.disabledLink.removeAttribute('aria-disabled');
    this.disabledLink.removeEventListener('click', this.blockDisabledNavigation);
    this.disabledLink.removeEventListener('keydown', this.blockDisabledNavigation);
    this.disabledLink = null;
    this.blockDisabledNavigation = null;
  }

  static contextKey() {
    const shop = window.Shopify?.shop || window.location.host || 'shop';
    const theme = window.Shopify?.theme?.id || 'theme';
    const country = window.Shopify?.country || 'country';
    const currency = window.Shopify?.currency?.active || 'currency';
    const language = document.documentElement?.lang || 'language';
    return [shop, theme, country, currency, language].map((value) => encodeURIComponent(String(value))).join(':');
  }

  static normalizeEndpoint(endpoint) {
    if (!endpoint) return '';
    const url = new URL(endpoint, window.location.origin);
    const entries = Array.from(url.searchParams.entries())
      .sort(([leftName, leftValue], [rightName, rightValue]) => (
        leftName.localeCompare(rightName) || leftValue.localeCompare(rightValue)
      ));
    url.search = '';
    entries.forEach(([name, value]) => url.searchParams.append(name, value));
    return `${url.pathname}${url.search}`;
  }

  static storageKey(kind, endpoints) {
    const normalized = endpoints.filter(Boolean).map((endpoint) => FwdCatalogCount.normalizeEndpoint(endpoint)).join('|');
    return `fwd:catalog-count:v${FwdCatalogCount.cacheSchemaVersion}:${FwdCatalogCount.contextKey()}:${kind}:${encodeURIComponent(normalized)}`;
  }

  static readStoredCount(key, ttl) {
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      const age = Date.now() - cached?.cachedAt;
      if (!FwdCatalogCount.validCount(cached?.count)
        || !Number.isFinite(age)
        || age < 0
        || age > FwdCatalogCount.staleCacheLimit) return null;
      return { count: cached.count, fresh: age <= ttl };
    } catch (_error) {
      return null;
    }
  }

  static writeStoredCount(key, count) {
    if (!FwdCatalogCount.validCount(count)) return;
    try {
      window.sessionStorage.setItem(key, JSON.stringify({ cachedAt: Date.now(), count }));
    } catch (_error) {
      // Counts remain available for this document when storage is unavailable.
    }
  }

  static readStoredDiscoverSummary(key) {
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      const age = Date.now() - cached?.cachedAt;
      if (!FwdCatalogCount.validDiscoverSummary(cached?.summary)
        || !Number.isFinite(age)
        || age < 0
        || age > FwdCatalogCount.staleCacheLimit) return null;
      return {
        summary: cached.summary,
        fresh: age <= FwdCatalogCount.discoverSummaryCacheTtl,
      };
    } catch (_error) {
      return null;
    }
  }

  static writeStoredDiscoverSummary(key, summary) {
    if (!FwdCatalogCount.validDiscoverSummary(summary)) return;
    try {
      window.sessionStorage.setItem(key, JSON.stringify({ cachedAt: Date.now(), summary }));
    } catch (_error) {
      // The in-memory request cache still serves every matching element in this document.
    }
  }

  static validCount(count) {
    return Number.isInteger(count?.available)
      && Number.isInteger(count?.total)
      && count.available >= 0
      && count.total >= 0
      && count.available <= count.total;
  }

  static validDiscoverSummary(summary) {
    return ['brand', 'country'].every((entity) => {
      const group = summary?.[entity];
      return FwdCatalogCount.validCount(group)
        && Array.isArray(group.top)
        && group.top.length <= 6
        && group.top.every((entry) => Number.isInteger(entry?.available)
          && entry.available > 0
          && typeof entry.gid === 'string'
          && entry.gid
          && typeof entry.label === 'string'
          && entry.label.trim()
          && typeof entry.url === 'string'
          && entry.url);
    });
  }

  static challengeKey() {
    return `fwd:catalog-count-challenge:v${FwdCatalogCount.cacheSchemaVersion}:${FwdCatalogCount.contextKey()}`;
  }

  static challengeIsActive() {
    try {
      const storedUntil = Number(window.sessionStorage.getItem(FwdCatalogCount.challengeKey()) || 0);
      return Math.max(storedUntil, FwdCatalogCount.challengeUntil) > Date.now();
    } catch (_error) {
      return FwdCatalogCount.challengeUntil > Date.now();
    }
  }

  static openChallengeCircuit() {
    FwdCatalogCount.challengeUntil = Date.now() + FwdCatalogCount.challengeCooldown;
    try {
      window.sessionStorage.setItem(
        FwdCatalogCount.challengeKey(),
        String(FwdCatalogCount.challengeUntil)
      );
    } catch (_error) {
      // A single document still stops retrying even when storage is unavailable.
    }
  }

  static challengeError(message = 'Catalog count challenge detected') {
    const error = new Error(message);
    error.retryable = false;
    error.challenge = true;
    return error;
  }

  static responseHasImmediateChallenge(response) {
    return response?.headers?.get?.('cf-mitigated') === 'challenge'
      || [403, 429].includes(response?.status);
  }

  static challengeBodyMatches(body) {
    return typeof body === 'string'
      && /(?:\/cdn-cgi\/challenge-platform|cf-chl-|just a moment(?:\u2026|\.{3})?|checking your browser|verify you are human)/i.test(body);
  }

  static async responseBodyMatchesChallenge(response) {
    if (!response || typeof response.text !== 'function') return false;
    try {
      return FwdCatalogCount.challengeBodyMatches(await response.text());
    } catch (_error) {
      return false;
    }
  }

  static withRequestSlot(loader, priority = 'normal') {
    return new Promise((resolve, reject) => {
      const task = { loader, resolve, reject };
      if (priority === 'high') FwdCatalogCount.requestQueue.unshift(task);
      else FwdCatalogCount.requestQueue.push(task);
      FwdCatalogCount.drainRequestQueue();
    });
  }

  static drainRequestQueue() {
    while (FwdCatalogCount.activeRequests < FwdCatalogCount.maxConcurrentRequests
      && FwdCatalogCount.requestQueue.length) {
      const task = FwdCatalogCount.requestQueue.shift();
      FwdCatalogCount.activeRequests += 1;
      Promise.resolve()
        .then(() => {
          if (FwdCatalogCount.challengeIsActive()) throw FwdCatalogCount.challengeError();
          return task.loader();
        })
        .then(task.resolve, task.reject)
        .finally(() => {
          FwdCatalogCount.activeRequests -= 1;
          FwdCatalogCount.drainRequestQueue();
        });
    }
  }

  static cachedRequest(cache, key, loader) {
    if (cache.has(key)) return cache.get(key);

    const request = Promise.resolve()
      .then(loader)
      .catch((error) => {
        if (cache.get(key) === request) cache.delete(key);
        throw error;
      });
    cache.set(key, request);
    return request;
  }

  static waitBeforeRetry(attempt) {
    return new Promise((resolve) => {
      setTimeout(resolve, FwdCatalogCount.retryDelayMilliseconds * attempt);
    });
  }

  static async fetchJson(endpoint, priority = 'normal') {
    let lastError;

    for (let attempt = 1; attempt <= FwdCatalogCount.maxRequestAttempts; attempt += 1) {
      try {
        if (FwdCatalogCount.challengeIsActive()) throw FwdCatalogCount.challengeError();
        const response = await FwdCatalogCount.withRequestSlot(() => (
          fetch(endpoint, { credentials: 'same-origin', cache: 'no-store' })
        ), priority);
        if (!response || typeof response.ok !== 'boolean') {
          const responseError = new TypeError('Catalog count response was invalid');
          responseError.retryable = false;
          throw responseError;
        }
        const inspectionResponse = typeof response.clone === 'function' ? response.clone() : null;
        if (FwdCatalogCount.responseHasImmediateChallenge(response)) {
          FwdCatalogCount.openChallengeCircuit();
          throw FwdCatalogCount.challengeError(`Catalog count challenge detected: ${response.status}`);
        }
        if (!response.ok) {
          if (response.status === 503
            && await FwdCatalogCount.responseBodyMatchesChallenge(inspectionResponse)) {
            FwdCatalogCount.openChallengeCircuit();
            throw FwdCatalogCount.challengeError(`Catalog count challenge detected: ${response.status}`);
          }
          const responseError = new Error(`Catalog count request failed: ${response.status}`);
          responseError.retryable = response.status >= 500;
          throw responseError;
        }
        if (typeof response.json !== 'function') {
          const responseError = new TypeError('Catalog count response was invalid');
          responseError.retryable = false;
          throw responseError;
        }
        let payload;
        try {
          payload = await response.json();
        } catch (_error) {
          if (await FwdCatalogCount.responseBodyMatchesChallenge(inspectionResponse)) {
            FwdCatalogCount.openChallengeCircuit();
            throw FwdCatalogCount.challengeError(`Catalog count challenge detected: ${response.status}`);
          }
          const responseError = new TypeError('Catalog count response was not JSON');
          responseError.retryable = false;
          throw responseError;
        }
        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
          const responseError = new TypeError('Catalog count payload must be an object');
          responseError.retryable = false;
          throw responseError;
        }
        return payload;
      } catch (error) {
        lastError = error;
        if (error.retryable === false || attempt === FwdCatalogCount.maxRequestAttempts) throw error;
        await FwdCatalogCount.waitBeforeRetry(attempt);
      }
    }

    throw lastError;
  }

  static fetchSharedJson(endpoint, priority = 'normal') {
    return FwdCatalogCount.cachedRequest(
      FwdCatalogCount.pageRequests,
      endpoint,
      () => FwdCatalogCount.fetchJson(endpoint, priority)
    );
  }

  static getDiscoverSummary(endpoint) {
    const storageKey = FwdCatalogCount.storageKey('discover-summary', [endpoint]);
    const cached = FwdCatalogCount.readStoredDiscoverSummary(storageKey);
    if (cached?.fresh || FwdCatalogCount.challengeIsActive()) {
      return { cached, request: null };
    }

    const request = FwdCatalogCount.cachedRequest(
      FwdCatalogCount.discoverSummaryRequests,
      endpoint,
      () => FwdCatalogCount.fetchDiscoverSummary(endpoint).then((summary) => {
        FwdCatalogCount.writeStoredDiscoverSummary(storageKey, summary);
        return summary;
      })
    );
    return { cached, request };
  }

  static async fetchDiscoverSummary(endpoint) {
    try {
      const { products, totals } = await FwdCatalogCount.fetchDiscoverPages(endpoint);
      const counts = { brand: new Map(), country: new Map() };

      products.forEach((product) => {
        if (!product || !Object.hasOwn(product, 'brand') || !Array.isArray(product.countries)) {
          throw new TypeError('Discover product relationships were incomplete');
        }
        if (product.brand !== null) {
          FwdCatalogCount.incrementDiscoverEntity(counts.brand, product.brand);
        }
        const countryIds = new Set();
        product.countries.forEach((country) => {
          if (!country || typeof country.gid !== 'string' || !country.gid) {
            throw new TypeError('Discover country relationship was incomplete');
          }
          if (countryIds.has(country.gid)) return;
          countryIds.add(country.gid);
          FwdCatalogCount.incrementDiscoverEntity(counts.country, country);
        });
      });

      const summarize = (entity, total) => {
        const entries = Array.from(counts[entity].values());
        if (entries.length > total) throw new TypeError('Discover entity total was inconsistent');
        return {
          available: entries.length,
          total,
          top: entries
            .sort((left, right) => right.available - left.available
              || left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }))
            .slice(0, 6),
        };
      };

      const summary = {
        brand: summarize('brand', totals.brands),
        country: summarize('country', totals.countries),
      };
      if (!FwdCatalogCount.validDiscoverSummary(summary)) {
        throw new TypeError('Discover summary was invalid');
      }
      return summary;
    } catch (error) {
      FwdCatalogCount.pageRequests.clear();
      throw error;
    }
  }

  static incrementDiscoverEntity(counts, entry) {
    if (!entry
      || typeof entry.gid !== 'string'
      || !entry.gid
      || typeof entry.label !== 'string'
      || !entry.label.trim()
      || typeof entry.url !== 'string'
      || !entry.url) {
      throw new TypeError('Discover entity relationship was incomplete');
    }
    const current = counts.get(entry.gid);
    if (current) {
      current.available += 1;
      return;
    }
    counts.set(entry.gid, {
      available: 1,
      gid: entry.gid,
      label: entry.label,
      url: entry.url,
    });
  }

  static async fetchDiscoverPages(endpoint) {
    const firstPayload = await FwdCatalogCount.fetchSharedJson(endpoint, 'high');
    const first = FwdCatalogCount.validateDiscoverPage(firstPayload, 1);
    if (first.pages === 1) return { products: first.products, totals: first.totals };

    const remainingProducts = new Array(first.pages - 1);
    let nextPage = 2;
    const fetchWorker = async () => {
      while (nextPage <= first.pages) {
        const page = nextPage;
        nextPage += 1;
        const payload = await FwdCatalogCount.fetchSharedJson(
          FwdCatalogCount.endpointWithPage(endpoint, page),
          'high'
        );
        const validated = FwdCatalogCount.validateDiscoverPage(payload, page, first.pages);
        if (validated.totals.brands !== first.totals.brands
          || validated.totals.countries !== first.totals.countries) {
          throw new TypeError('Discover totals changed during pagination');
        }
        remainingProducts[page - 2] = validated.products;
      }
    };
    const workerCount = Math.min(FwdCatalogCount.maxConcurrentPageRequests, first.pages - 1);
    await Promise.all(Array.from({ length: workerCount }, () => fetchWorker()));
    return {
      products: first.products.concat(...remainingProducts),
      totals: first.totals,
    };
  }

  static validateDiscoverPage(payload, expectedPage, expectedPages) {
    const validated = FwdCatalogCount.validatePage(payload, 'products', expectedPage, expectedPages);
    const totals = payload?.totals;
    if (!Number.isInteger(totals?.brands)
      || totals.brands < 0
      || !Number.isInteger(totals?.countries)
      || totals.countries < 0) {
      throw new TypeError('Discover totals were incomplete');
    }
    return { products: validated.entries, pages: validated.pages, totals };
  }

  static async fetchProductCount(endpoint, totalEndpoint) {
    try {
      const [availabilityPayload, totalPayload] = await Promise.all([
        FwdCatalogCount.fetchSharedJson(endpoint),
        totalEndpoint ? FwdCatalogCount.fetchSharedJson(totalEndpoint) : Promise.resolve(null),
      ]);
      const availableCount = availabilityPayload.available;
      const totalCount = totalPayload ? totalPayload.total : availabilityPayload.total;
      if (
        !Number.isInteger(availableCount)
        || !Number.isInteger(totalCount)
        || availableCount < 0
        || totalCount < 0
        || availableCount > totalCount
      ) {
        throw new TypeError('Catalog count response must contain non-negative integers');
      }
      return {
        available: availableCount,
        total: totalCount,
      };
    } catch (error) {
      FwdCatalogCount.pageRequests.clear();
      throw error;
    }
  }

  static endpointWithPage(endpoint, page) {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('page', String(page));
    return url.toString();
  }

  static endpointFromNextUrl(endpoint, nextUrl) {
    const source = new URL(endpoint, window.location.origin);
    const next = new URL(nextUrl, window.location.origin);
    const view = source.searchParams.get('view');
    if (view && !next.searchParams.has('view')) next.searchParams.set('view', view);
    return next.toString();
  }

  static async fetchPages(endpoint, listKey) {
    const firstPayload = await FwdCatalogCount.fetchSharedJson(endpoint);
    const { entries: firstEntries, pages } = FwdCatalogCount.validatePage(
      firstPayload,
      listKey,
      1
    );
    if (pages === 1) return firstEntries;

    if (typeof firstPayload.nextUrl === 'string' && firstPayload.nextUrl) {
      return FwdCatalogCount.fetchSequentialPages(
        endpoint,
        listKey,
        firstEntries,
        pages,
        firstPayload.nextUrl
      );
    }

    const remainingEntries = new Array(pages - 1);
    let nextPage = 2;
    const fetchWorker = async () => {
      while (nextPage <= pages) {
        const page = nextPage;
        nextPage += 1;
        const payload = await FwdCatalogCount.fetchSharedJson(
          FwdCatalogCount.endpointWithPage(endpoint, page)
        );
        remainingEntries[page - 2] = FwdCatalogCount.validatePage(
          payload,
          listKey,
          page,
          pages
        ).entries;
      }
    };
    const workerCount = Math.min(FwdCatalogCount.maxConcurrentPageRequests, pages - 1);
    await Promise.all(Array.from({ length: workerCount }, () => fetchWorker()));
    return firstEntries.concat(...remainingEntries);
  }

  static validatePage(payload, listKey, expectedPage, expectedPages) {
    const page = payload.page;
    const pages = payload.pages;
    if (
      !Number.isInteger(page)
      || page !== expectedPage
      || !Number.isInteger(pages)
      || pages < page
      || (expectedPages !== undefined && pages !== expectedPages)
      || !Array.isArray(payload[listKey])
    ) {
      throw new TypeError('Catalog count pagination response was incomplete');
    }
    return { entries: payload[listKey], pages };
  }

  static async fetchSequentialPages(endpoint, listKey, firstEntries, pages, firstNextUrl) {
    const entries = [...firstEntries];
    const visited = new Set([endpoint]);
    let expectedPage = 2;
    let nextEndpoint = FwdCatalogCount.endpointFromNextUrl(endpoint, firstNextUrl);

    while (expectedPage <= pages) {
      if (visited.has(nextEndpoint)) throw new Error('Catalog count pagination looped');
      visited.add(nextEndpoint);
      const payload = await FwdCatalogCount.fetchSharedJson(nextEndpoint);
      const validated = FwdCatalogCount.validatePage(payload, listKey, expectedPage, pages);
      entries.push(...validated.entries);
      if (expectedPage < pages) {
        nextEndpoint = typeof payload.nextUrl === 'string' && payload.nextUrl
          ? FwdCatalogCount.endpointFromNextUrl(endpoint, payload.nextUrl)
          : FwdCatalogCount.endpointWithPage(endpoint, expectedPage + 1);
      }
      expectedPage += 1;
    }

    return entries;
  }

  static async fetchEntityCount(entity, directoryEndpoint, productsEndpoint) {
    const summary = await FwdCatalogCount.fetchEntitySummaryShared(
      entity,
      directoryEndpoint,
      productsEndpoint
    );
    return { available: summary.available, total: summary.total };
  }

  static fetchEntitySummaryShared(entity, directoryEndpoint, productsEndpoint) {
    const requestKey = `${entity}:${directoryEndpoint}:${productsEndpoint}`;
    return FwdCatalogCount.cachedRequest(
      FwdCatalogCount.entitySummaryRequests,
      requestKey,
      () => FwdCatalogCount.fetchEntitySummary(entity, directoryEndpoint, productsEndpoint)
    );
  }

  static async fetchEntitySummary(entity, directoryEndpoint, productsEndpoint) {
    try {
      const directoryKey = entity === 'brand' ? 'brands' : 'countries';
      const [directoryEntries, products] = await Promise.all([
        FwdCatalogCount.fetchPages(directoryEndpoint, directoryKey),
        FwdCatalogCount.fetchPages(productsEndpoint, 'products'),
      ]);

      const directoryById = new Map();
      directoryEntries.forEach((entry) => {
        if (!entry || typeof entry.gid !== 'string' || !entry.gid) {
          throw new TypeError('Catalog directory entity was incomplete');
        }
        directoryById.set(entry.gid, entry);
      });

      const availableCounts = new Map();
      products.forEach((product) => {
        if (!product || typeof product.available !== 'boolean') {
          throw new TypeError('Catalog entity product was incomplete');
        }
        if (!product.available) return;

        const productEntityIds = entity === 'brand' ? [product.brandId] : product.countryIds;
        if (
          !Array.isArray(productEntityIds)
          || productEntityIds.some((entityId) => typeof entityId !== 'string' || !entityId)
        ) {
          throw new TypeError('Catalog entity relationship was incomplete');
        }
        new Set(productEntityIds).forEach((entityId) => {
          if (!directoryById.has(entityId)) return;
          availableCounts.set(entityId, (availableCounts.get(entityId) || 0) + 1);
        });
      });

      const top = Array.from(availableCounts, ([gid, available]) => {
        const entry = directoryById.get(gid);
        const label = entity === 'country' ? entry.displayLabel || entry.label : entry.label;
        return { available, gid, label, url: entry.url };
      })
        .filter((entry) => typeof entry.label === 'string' && entry.label.trim()
          && typeof entry.url === 'string' && entry.url)
        .sort((left, right) => right.available - left.available
          || left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }))
        .slice(0, 6);

      return { available: availableCounts.size, total: directoryById.size, top };
    } catch (error) {
      FwdCatalogCount.pageRequests.clear();
      throw error;
    }
  }
}

class FwdCatalogTopEntities extends HTMLElement {
  connectedCallback() {
    if (this.dataset.desktopOnly === 'true') {
      this.desktopMedia = window.matchMedia('(min-width: 990px)');
      if (!this.desktopMedia.matches) {
        this.dataset.state = 'deferred';
        this.hidden = true;
        this.handleDesktopMediaChange = (event) => {
          if (!event.matches) return;
          this.removeDesktopMediaListener();
          this.load();
        };
        if (typeof this.desktopMedia.addEventListener === 'function') {
          this.desktopMedia.addEventListener('change', this.handleDesktopMediaChange);
        } else {
          this.desktopMedia.addListener?.(this.handleDesktopMediaChange);
        }
        return;
      }
    }
    this.load();
  }

  disconnectedCallback() {
    this.removeDesktopMediaListener();
  }

  removeDesktopMediaListener() {
    if (!this.desktopMedia || !this.handleDesktopMediaChange) return;
    if (typeof this.desktopMedia.removeEventListener === 'function') {
      this.desktopMedia.removeEventListener('change', this.handleDesktopMediaChange);
    } else {
      this.desktopMedia.removeListener?.(this.handleDesktopMediaChange);
    }
    this.handleDesktopMediaChange = null;
  }

  load() {
    const {
      entity,
      summaryEndpoint,
      directoryEndpoint,
      productsEndpoint,
      allUrl,
      allLabel,
    } = this.dataset;
    if (!['brand', 'country'].includes(entity)
      || (!summaryEndpoint && (!directoryEndpoint || !productsEndpoint))
      || !allUrl
      || !allLabel) return;

    const result = summaryEndpoint
      ? FwdCatalogCount.getDiscoverSummary(summaryEndpoint)
      : {
        cached: null,
        request: FwdCatalogCount.fetchEntitySummaryShared(entity, directoryEndpoint, productsEndpoint),
      };
    if (result.cached) {
      this.render(result.cached.summary[entity].top, entity, allUrl, allLabel);
      this.dataset.state = result.cached.fresh ? 'loaded' : 'stale';
    } else {
      this.dataset.state = 'loading';
      this.setAttribute('aria-busy', 'true');
      this.hidden = true;
    }
    if (!result.request) return;

    result.request
      .then((summary) => {
        if (!this.isConnected) return;
        this.render(summaryEndpoint ? summary[entity].top : summary.top, entity, allUrl, allLabel);
      })
      .catch(() => {
        if (!this.isConnected) return;
        if (result.cached) return;
        this.dataset.state = 'unavailable';
        this.setAttribute('aria-busy', 'false');
        this.hidden = true;
      });
  }

  render(entries, entity, allUrl, allLabel) {
    if (!Array.isArray(entries) || entries.length === 0) {
      this.dataset.state = 'empty';
      this.setAttribute('aria-busy', 'false');
      this.hidden = true;
      return;
    }

    const entityLabel = entity === 'brand' ? '브랜드' : '제조국';
    const rankedLinks = entries.map((entry) => {
      const label = String(entry.label).replace(/FWD/g, 'fwd');
      return `<span class="fwd-nav__top-entity-item" role="listitem"><a class="fwd-nav__top-entity-link" href="${FwdCatalogTopEntities.escapeMarkup(entry.url)}"><span>${FwdCatalogTopEntities.escapeMarkup(label)}</span><span class="fwd-nav__top-entity-count" aria-label="판매 중 상품 ${entry.available}개">(${entry.available})</span></a></span>`;
    }).join('');
    const allLink = `<span class="fwd-nav__top-entity-item fwd-nav__top-entity-item--all" role="listitem"><a class="fwd-nav__top-entity-link" href="${FwdCatalogTopEntities.escapeMarkup(allUrl)}">${FwdCatalogTopEntities.escapeMarkup(allLabel)}</a></span>`;
    this.innerHTML = rankedLinks + allLink;
    this.dataset.state = 'loaded';
    this.setAttribute('aria-busy', 'false');
    this.setAttribute('aria-label', `판매 중 상품 수 상위 ${entries.length}개 ${entityLabel} 및 전체 목록`);
    this.hidden = false;
  }

  static escapeMarkup(value) {
    return String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[character]);
  }
}

if (!customElements.get('fwd-navigation')) {
  customElements.define('fwd-navigation', FwdNavigation);
}

if (!customElements.get('fwd-catalog-top-entities')) {
  customElements.define('fwd-catalog-top-entities', FwdCatalogTopEntities);
}

if (!customElements.get('fwd-catalog-count')) {
  customElements.define('fwd-catalog-count', FwdCatalogCount);
}

window.FwdCatalogCountGuard = {
  challengeIsActive: () => FwdCatalogCount.challengeIsActive(),
  challengeBodyMatches: (body) => FwdCatalogCount.challengeBodyMatches(body),
  openChallengeCircuit: () => FwdCatalogCount.openChallengeCircuit(),
  responseHasImmediateChallenge: (response) => FwdCatalogCount.responseHasImmediateChallenge(response),
};
